// src/Components/ImportMarkers/ImportExcel.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Divider,
  CircularProgress,
  TextField,
  Alert,
  Dialog,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';

// Navigation Icons
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import {
  MenuAddNew,
  MenuAddNewGrey,
  MenuSave,
  MenuSaveGrey,
  MenuDelete,
  MenuDeleteGrey,
  MenuIconCancel,
  MenuIconCancelGrey,
} from '../../assets/icons/CustomIcons';

import {
  MenuImport,
  MenuImportGrey,
  MenuUpload,
  MenuUploadGrey,
} from '../../assets/icons/CustomIconsAdd2';

import {
  MenuReinstate,
  MenuReinstateGrey,
  MenuArchive,
  MenuArchiveGrey,
} from '../../assets/icons/CustomIconsAdd4';

// Import Custom Components
import ExcelMenu from './ExcelMenu';
import ExcelAssignData from './ExcelAssignData';
import ImportPhotos from './ImportPhotos';
import ExcelHistoryView from './ExcelHistoryView';
import ExcelDataView from './ExcelDataView';
import ExcelDialogs from './ExcelDialogs';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import CoordinateMatchDialog from './CoordinateMatchDialog';
import DuplicateCheckOnImport from './DuplicateCheckOnImport';

// Import Hook
import { useImportExcelManage } from '../../Hooks/useImportExcelManage';

// Import Image
import AdminBg from '../../assets/images/AdminBackground.png';

const STEPS = [
  'MENU_VIEW',
  'DATA_VIEW',
  'DUPLICATE_CHECK_VIEW',
  'SETTINGS_VIEW',
  'HISTORY_VIEW',
];

const ICON_SIZE = 64;

// Excel Icon Settings
const PAGE1_UPLOAD_SCALE = 4.9;
const PAGE1_UPLOAD_ICON_X = 25;
const PAGE1_UPLOAD_ICON_Y = 38;
const PAGE1_UPLOAD_TEXT_SIZE = '18px';
const PAGE1_UPLOAD_TEXT_Y = 108;

const PAGE2_IMPORT_SCALE = 7.5;
const PAGE2_IMPORT_ICON_X = 25;
const PAGE2_IMPORT_ICON_Y = 37;
const PAGE2_IMPORT_TEXT_SIZE = '18px';
const PAGE2_IMPORT_TEXT_Y = 108;

export default function ImportExcel({ setIsDirty, kmlHasData }) {
  const { state, flags, alerts, actions } = useImportExcelManage({ setIsDirty });

  const isKmlLoaded = Boolean(kmlHasData);

  const [tempBatchFilename, setTempBatchFilename] = useState('');

  useEffect(() => {
    if (alerts?.showBatchFilenameDialog) {
      setTempBatchFilename(state?.currentFilename || '');
    }
  }, [alerts?.showBatchFilenameDialog, state?.currentFilename]);

  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  const isStepNavDisabled = !state?.selectedMenuId;

  const isAddNewDisabled =
    state?.viewMode === 'HISTORY_VIEW' ||
    state?.viewMode === 'DUPLICATE_CHECK_VIEW' ||
    state?.isProcessing;

  const canCancel =
    flags?.hasData || (state?.viewMode === 'SETTINGS_VIEW' && state?.isMenuDirty);

  // --- SUB-PANEL DYNAMICS ---
  const activeRowId =
    state?.resolveCoordsState?.rowId ||
    state?.invalidMatchState?.rowId ||
    state?.manualMatchRowId ||
    state?.documentMatchRowId;

  const activeRowData = state?.parsedData?.find((r) => r.id === activeRowId);
  const isCoordsActive = Boolean(state?.resolveCoordsState?.open);
  const isInvalidMatchActive = Boolean(state?.invalidMatchState?.open);
  const isManualMatchActive = !!state?.manualMatchRowId;
  const isDocumentMatchActive = !!state?.documentMatchRowId;
  const isTypeDesc = state?.invalidMatchState?.type === 'DESCRIPTION';

  const isSubPanelActive = Boolean(
    (isCoordsActive ||
      isInvalidMatchActive ||
      isManualMatchActive ||
      isDocumentMatchActive) &&
    state?.viewMode === 'DATA_VIEW',
  );

  const closeSubPanel = () => {
    actions.setInvalidMatchState({
      open: false,
      rowId: null,
      colName: null,
      cellValue: '',
      type: null,
      searchTarget: '',
    });
    actions.setResolveCoordsState({
      open: false,
      rowId: null,
      searchTarget: '',
      actualTitle: '',
      geoSuffix: '',
    });
    actions.setManualMatchRowId(null);
    if (actions.setDocumentMatchRowId) actions.setDocumentMatchRowId(null);
  };

  // --- DYNAMIC COORDINATE PARSER FOR MAP CENTERING ---
  let activeLat = activeRowData?.lat;
  let activeLng = activeRowData?.lng;

  if (isSubPanelActive && (!activeLat || !activeLng)) {
    const posKey = Object.keys(state?.rowMappings || {}).find(
      (k) =>
        state.rowMappings[k] === 'POSITION (LAT, LNG)' ||
        state.rowMappings[k] === 'POSITION',
    );
    let posCol = null;
    if (posKey) {
      if (posKey.startsWith('prop_')) posCol = posKey.substring(5);
      else {
        const cr = state?.customRows?.find((r) => r.id === posKey);
        if (cr) posCol = cr.name;
      }
    }
    if (posCol && activeRowData?.[posCol]) {
      const matches = String(activeRowData[posCol]).match(/-?\d+(\.\d+)?/g);
      if (matches && matches.length >= 2) {
        activeLat = matches[0];
        activeLng = matches[1];
      }
    }

    if (!activeLat || !activeLng) {
      activeLat =
        activeRowData?.['Latitude'] ??
        activeRowData?.['latitude'] ??
        activeRowData?.['Lat'] ??
        activeRowData?.['lat'] ??
        activeRowData?.['LAT'] ??
        activeRowData?.['LATITUDE'];

      activeLng =
        activeRowData?.['Longitude'] ??
        activeRowData?.['longitude'] ??
        activeRowData?.['Lng'] ??
        activeRowData?.['lng'] ??
        activeRowData?.['LNG'] ??
        activeRowData?.['LONGITUDE'] ??
        activeRowData?.['Lon'] ??
        activeRowData?.['lon'];
    }
  }

  // --- REORDER LOGIC FOR PAGE 3 ---
  const handleMoveUp = () => {
    if (!state?.selectedCustomIds || state.selectedCustomIds.length === 0) return;

    if (state.selectedCustomIds.length > 1) {
      actions.setAlertState({
        open: true,
        message: 'Please select one row at a time to move',
        severity: 'warning',
      });
      return;
    }

    const newRows = [...state.customRows];
    let moved = false;

    for (let i = 1; i < newRows.length; i++) {
      if (state.selectedCustomIds.includes(newRows[i].id)) {
        if (!state.selectedCustomIds.includes(newRows[i - 1].id)) {
          const temp = newRows[i - 1];
          newRows[i - 1] = newRows[i];
          newRows[i] = temp;
          moved = true;
        }
      }
    }

    if (moved) {
      actions.setCustomRows(newRows);
      actions.setIsMenuDirty(true);
    }
  };

  const handleMoveDown = () => {
    if (!state?.selectedCustomIds || state.selectedCustomIds.length === 0) return;

    if (state.selectedCustomIds.length > 1) {
      actions.setAlertState({
        open: true,
        message: 'Please select one row at a time to move',
        severity: 'warning',
      });
      return;
    }

    const newRows = [...state.customRows];
    let moved = false;

    for (let i = newRows.length - 2; i >= 0; i--) {
      if (state.selectedCustomIds.includes(newRows[i].id)) {
        if (!state.selectedCustomIds.includes(newRows[i + 1].id)) {
          const temp = newRows[i + 1];
          newRows[i + 1] = newRows[i];
          newRows[i] = temp;
          moved = true;
        }
      }
    }

    if (moved) {
      actions.setCustomRows(newRows);
      actions.setIsMenuDirty(true);
    }
  };

  const selectedHistoryBatches =
    state?.importHistory?.filter((b) => state?.selectedHistoryIds?.includes(b.id)) || [];
  const totalMarkersToDelete = selectedHistoryBatches.reduce(
    (acc, batch) => acc + (batch.markerCount || 0),
    0,
  );
  const batchCount = state?.selectedHistoryIds?.length || 0;
  const batchText = batchCount === 1 ? 'batch' : 'batches';

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}>
      {/* --- IMPORT PROGRESS OVERLAY --- */}
      <Dialog
        open={Boolean(
          state?.isProcessing &&
          (state?.uploadProgress?.total > 0 || state?.progressMessage),
        )}
        disableEscapeKeyDown
        sx={{
          zIndex: 100015,
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            paddingTop: '96px',
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: 'none',
            m: 0,
          },
        }}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } },
        }}>
        <Alert
          severity='info'
          icon={<CircularProgress size={24} thickness={4} sx={{ color: '#0288d1' }} />}
          sx={{
            minWidth: '350px',
            alignItems: 'center',
            fontWeight: 500,
            border: '1px solid rgba(0,0,0,0.1)',
            boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
            fontSize: '1rem',
            backgroundColor: '#e3f2fd',
            color: '#014361',
          }}
          action={
            <IconButton
              size='small'
              onClick={actions.handleCancelProcess}
              disabled={state?.isCancelling}
              sx={{
                color: '#d32f2f',
                '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' },
              }}
              title={state?.isCancelling ? 'Stopping...' : 'Cancel Import'}>
              <CancelIcon fontSize='inherit' sx={{ fontSize: '28px' }} />
            </IconButton>
          }>
          {state?.isCancelling
            ? 'Stopping process...'
            : state?.progressMessage
              ? state.progressMessage
              : `Importing ${state?.uploadProgress?.current || 0} / ${state?.uploadProgress?.total || 0} markers...`}
        </Alert>
      </Dialog>

      {/* Hidden File Input */}
      <input
        type='file'
        accept='.xlsx, .xls, .csv'
        ref={state?.fileInputRef}
        onChange={actions.handleFileChange}
        style={{ display: 'none' }}
      />

      {/* --- TOP TOOLBAR --- */}
      <Box
        sx={{
          height: '53px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          paddingLeft: '5px',
          paddingRight: '5px',
        }}>
        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            disabled={isAddNewDisabled}
            onClick={
              state?.viewMode === 'MENU_VIEW'
                ? actions.handleAddNewMenu
                : state?.viewMode === 'DATA_VIEW'
                  ? actions.handleAddDataRow
                  : actions.handleAddCustomRow
            }
            title={
              state?.viewMode === 'MENU_VIEW'
                ? 'Add New Menu'
                : state?.viewMode === 'DATA_VIEW'
                  ? 'Add Data Row'
                  : 'Add Custom Row'
            }>
            {!isAddNewDisabled ? (
              <MenuAddNew sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuAddNewGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <IconButton
              sx={{ p: 0, transform: 'translateY(-5px)' }}
              onClick={actions.handleUploadClick}
              disabled={
                state?.isProcessing ||
                flags?.hasData ||
                state?.viewMode !== 'DATA_VIEW' ||
                isKmlLoaded
              }
              title={isKmlLoaded ? 'Clear KML Import data first' : 'Upload Excel File'}>
              {state?.viewMode === 'DATA_VIEW' &&
              !state?.isProcessing &&
              !flags?.hasData &&
              !isKmlLoaded ? (
                <MenuUpload
                  sx={{ fontSize: ICON_SIZE }}
                  iconScale={PAGE1_UPLOAD_SCALE}
                  iconX={PAGE1_UPLOAD_ICON_X}
                  iconY={PAGE1_UPLOAD_ICON_Y}
                  textSize={PAGE1_UPLOAD_TEXT_SIZE}
                  textY={PAGE1_UPLOAD_TEXT_Y}
                  label='Upload'
                />
              ) : (
                <MenuUploadGrey
                  sx={{ fontSize: ICON_SIZE }}
                  iconScale={PAGE1_UPLOAD_SCALE}
                  iconX={PAGE1_UPLOAD_ICON_X}
                  iconY={PAGE1_UPLOAD_ICON_Y}
                  textSize={PAGE1_UPLOAD_TEXT_SIZE}
                  textY={PAGE1_UPLOAD_TEXT_Y}
                  label='Upload'
                />
              )}
            </IconButton>
          </Box>
        </Box>

        <Box sx={toolBlockStyle}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <IconButton
              sx={{ p: 0, transform: 'translateY(-5px)' }}
              onClick={actions.handleSave}
              disabled={
                !flags?.hasData ||
                !state?.parsedData ||
                state.parsedData.length === 0 ||
                state?.isProcessing ||
                state?.viewMode !== 'DATA_VIEW'
              }
              title='Import Data to Map'>
              {flags?.hasData &&
              state?.parsedData?.length > 0 &&
              !state?.isProcessing &&
              state?.viewMode === 'DATA_VIEW' ? (
                <MenuImport
                  sx={{ fontSize: ICON_SIZE }}
                  iconScale={PAGE2_IMPORT_SCALE}
                  iconX={PAGE2_IMPORT_ICON_X}
                  iconY={PAGE2_IMPORT_ICON_Y}
                  textSize={PAGE2_IMPORT_TEXT_SIZE}
                  textY={PAGE2_IMPORT_TEXT_Y}
                  label='Import'
                />
              ) : (
                <MenuImportGrey
                  sx={{ fontSize: ICON_SIZE }}
                  iconScale={PAGE2_IMPORT_SCALE}
                  iconX={PAGE2_IMPORT_ICON_X}
                  iconY={PAGE2_IMPORT_ICON_Y}
                  textSize={PAGE2_IMPORT_TEXT_SIZE}
                  textY={PAGE2_IMPORT_TEXT_Y}
                  label='Import'
                />
              )}
            </IconButton>
          </Box>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={actions.handleSaveMenu}
            disabled={!state?.isMenuDirty || state?.isProcessing}>
            {state?.isMenuDirty && !state?.isProcessing ? (
              <MenuSave sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            disabled={
              (!flags?.canDeleteData && state?.viewMode !== 'DATA_VIEW') ||
              state?.isProcessing ||
              (state?.viewMode === 'DATA_VIEW' && !flags?.hasData)
            }
            onClick={actions.handleDeleteClick}
            title='Delete Selected'>
            {flags?.canDeleteData ||
            (state?.viewMode === 'DATA_VIEW' && flags?.hasData) ? (
              <MenuDelete sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={{ ...toolBlockStyle, position: 'relative' }}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={canCancel ? actions.handleCancelClick : undefined}
            disabled={
              state?.isProcessing ||
              !canCancel ||
              state?.viewMode === 'HISTORY_VIEW' ||
              state?.viewMode === 'MENU_VIEW'
            }
            title='Cancel'>
            {canCancel &&
            state?.viewMode !== 'HISTORY_VIEW' &&
            state?.viewMode !== 'MENU_VIEW' &&
            !state?.isProcessing ? (
              <MenuIconCancel sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuIconCancelGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>
      </Box>

      {/* --- SETTINGS VIEW SUB-HEADER FOR REORDERING --- */}
      {state?.viewMode === 'SETTINGS_VIEW' && (
        <Box
          sx={{
            height: '55px',
            minHeight: '55px',
            maxHeight: '55px',
            overflow: 'hidden',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f9f9f9',
            borderBottom: '1px solid #ccc',
            flexShrink: 0,
            zIndex: 49,
            px: 0,
          }}>
          <Box
            sx={{
              flex: 4,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              px: 2,
              py: 0.5,
            }}>
            <Typography
              sx={{
                fontSize: '0.85rem',
                fontStyle: 'italic',
                color: 'text.secondary',
              }}>
              Select a property below and use the arrows to move it up or down.
            </Typography>
          </Box>

          <Box sx={{ ...toolBlockStyle, transform: 'translateX(-10px)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconButton
                onClick={handleMoveUp}
                disabled={!state?.selectedCustomIds?.length}
                title='Move Selected Up'
                sx={{
                  mr: 1,
                  width: '26px',
                  height: '26px',
                  border: '1px solid',
                  borderColor: state?.selectedCustomIds?.length ? '#1976d2' : '#ccc',
                  backgroundColor: state?.selectedCustomIds?.length
                    ? 'lightskyblue'
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: state?.selectedCustomIds?.length
                      ? '#87CEFA'
                      : 'transparent',
                    opacity: 0.8,
                  },
                }}>
                <ArrowUpwardIcon
                  sx={{
                    fontSize: '24px',
                    color: state?.selectedCustomIds?.length ? '#333' : 'grey',
                  }}
                />
              </IconButton>
              <IconButton
                onClick={handleMoveDown}
                disabled={!state?.selectedCustomIds?.length}
                title='Move Selected Down'
                sx={{
                  width: '26px',
                  height: '26px',
                  border: '1px solid',
                  borderColor: state?.selectedCustomIds?.length ? '#1976d2' : '#ccc',
                  backgroundColor: state?.selectedCustomIds?.length
                    ? 'lightskyblue'
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: state?.selectedCustomIds?.length
                      ? '#87CEFA'
                      : 'transparent',
                    opacity: 0.8,
                  },
                }}>
                <ArrowDownwardIcon
                  sx={{
                    fontSize: '24px',
                    color: state?.selectedCustomIds?.length ? '#333' : 'grey',
                  }}
                />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* --- SECONDARY HEADER FOR HISTORY VIEW --- */}
      {state?.viewMode === 'HISTORY_VIEW' && (
        <Box
          sx={{
            height: '55px',
            minHeight: '55px',
            maxHeight: '55px',
            overflow: 'hidden',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#f9f9f9',
            borderBottom: '1px solid #ccc',
            flexShrink: 0,
            zIndex: 49,
            px: 2,
            py: 0.5,
            boxSizing: 'border-box',
          }}>
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              pr: 2,
              gap: 0.5,
            }}></Box>

          <Divider orientation='vertical' flexItem sx={{ my: 1, borderColor: '#ccc' }} />

          <Box
            sx={{
              width: '110px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              ml: 1,
            }}>
            <IconButton
              sx={{ p: 0, transform: 'translateY(5px)' }}
              disabled={!flags?.canUndoHistory || state?.selectedHistoryIds?.length === 0}
              onClick={actions.handleReinstateClick}
              title='Reinstate Selected Batches'>
              {flags?.canUndoHistory && state?.selectedHistoryIds?.length > 0 ? (
                <MenuReinstate sx={{ fontSize: ICON_SIZE * 1.5 }} />
              ) : (
                <MenuReinstateGrey sx={{ fontSize: ICON_SIZE * 1.5 }} />
              )}
            </IconButton>
          </Box>

          <Divider orientation='vertical' flexItem sx={{ my: 1, borderColor: '#ccc' }} />

          <Box
            sx={{
              width: '110px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              ml: 1,
            }}>
            <IconButton
              sx={{ p: 0, transform: 'translateY(-5px)' }}
              disabled={!flags?.canUndoHistory}
              onClick={actions.handlePublishClick}>
              {flags?.canUndoHistory ? (
                <MenuArchive sx={{ fontSize: ICON_SIZE * 1.5 }} />
              ) : (
                <MenuArchiveGrey sx={{ fontSize: ICON_SIZE * 1.5 }} />
              )}
            </IconButton>
          </Box>
        </Box>
      )}

      {/* --- WORKSPACE WRAPPER --- */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
        {/* --- DYNAMIC CONTENT AREA --- */}
        <Box
          sx={{
            height: isSubPanelActive ? '50%' : '100%',
            transition: 'height 0.3s ease-in-out',
            p: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            mt: 0,
            boxSizing: 'border-box',
          }}>
          {state?.viewMode === 'MENU_VIEW' && (
            <ExcelMenu
              localMenus={state.localMenus}
              selectedMenuId={state.selectedMenuId}
              handleSelectMenu={actions.handleSelectMenu}
              handleMenuTitleChange={actions.handleMenuTitleChange}
              isLoadingMenus={state.isLoadingMenus}
              makeUppercase={state.makeUppercase}
              handleCopyMenu={actions.handleCopyMenu}
              handlePasteMenu={actions.handlePasteMenu}
              copiedMenuTemplate={state.copiedMenuTemplate}
            />
          )}

          {state?.viewMode === 'DATA_VIEW' && (
            <ExcelDataView
              hasData={flags.hasData}
              parsedData={state.parsedData}
              columns={state.columns}
              successfullyImportedIds={state.successfullyImportedIds}
              selectedRowIds={state.selectedRowIds}
              handleSelectAllRows={actions.handleSelectAllRows}
              handleSelectRow={actions.handleSelectRow}
              rowMappings={state.rowMappings}
              rowSubMappings={state.rowSubMappings}
              customRows={state.customRows}
              regions={state.regions}
              categories={state.categories}
              markerIcons={state.markerIcons}
              setInvalidMatchState={actions.setInvalidMatchState}
              setManualMatchRowId={actions.setManualMatchRowId}
              setResolveCoordsState={actions.setResolveCoordsState}
              makeUppercase={state.makeUppercase}
              activeRowId={activeRowId}
              pendingNextColumn={state.pendingNextColumn}
              setPendingNextColumn={actions.setPendingNextColumn}
              invalidMatchState={state.invalidMatchState}
              resolveCoordsState={state.resolveCoordsState}
              manualMatchRowId={state.manualMatchRowId}
              pendingReinstateBatchIds={state.pendingReinstateBatchIds}
              setDocumentMatchRowId={actions.setDocumentMatchRowId}
              documentMatchRowId={state.documentMatchRowId}
              handleDeleteColumnClick={actions.handleDeleteColumnClick}
              localMenus={state.localMenus} // AMENDED: Pass menus to scan historical mappings
            />
          )}

          {state?.viewMode === 'DUPLICATE_CHECK_VIEW' && (
            <DuplicateCheckOnImport stateStore={{ state, actions, setters: actions }} />
          )}

          {state?.viewMode === 'SETTINGS_VIEW' && (
            <ExcelAssignData
              hasData={flags.hasData}
              columns={state.columns}
              savedMenus={state.savedMenus}
              categories={state.categories}
              regions={state.regions}
              globalCategory={state.globalCategory}
              setGlobalCategory={actions.setGlobalCategory}
              customRows={state.customRows}
              handleCustomRowNameChange={actions.handleCustomRowNameChange}
              markerIcons={state.markerIcons}
              selectedCustomIds={state.selectedCustomIds}
              setSelectedCustomIds={actions.setSelectedCustomIds}
              rowMappings={state.rowMappings}
              setRowMappings={actions.setRowMappings}
              rowSubMappings={state.rowSubMappings}
              setRowSubMappings={actions.setRowSubMappings}
              setRowStaticValues={actions.setRowStaticValues}
              rowStaticValues={state.rowStaticValues}
              defaultPublicFromDate={state.defaultPublicFromDate}
              setIsMenuDirty={actions.setIsMenuDirty}
              makeUppercase={state.makeUppercase}
            />
          )}

          {state?.viewMode === 'HISTORY_VIEW' && (
            <ExcelHistoryView
              importHistory={state.importHistory}
              isLoadingHistory={state.isLoadingHistory}
              selectedHistoryIds={state.selectedHistoryIds}
              setSelectedHistoryIds={actions.setSelectedHistoryIds}
            />
          )}
        </Box>

        {/* --- SCALABLE SUB-PANEL CONTAINER --- */}
        <Box
          sx={{
            height: isSubPanelActive ? '50%' : '0px',
            transition: 'height 0.3s ease-in-out',
            flexShrink: 0,
            borderTop: isSubPanelActive ? '1px solid #ccc' : 'none',
            backgroundColor: '#fff',
            position: 'relative',
            display: 'flex',
            zIndex: 10,
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}>
          {isSubPanelActive && (
            <>
              <IconButton
                size='small'
                onClick={closeSubPanel}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 20,
                  color: 'text.secondary',
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                }}>
                <CloseIcon fontSize='small' />
              </IconButton>

              <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                <CoordinateMatchDialog
                  open={isSubPanelActive}
                  isCoordsActive={isCoordsActive}
                  isManualMatchActive={isManualMatchActive}
                  isTypeDesc={isTypeDesc}
                  onConfirm={(lat, lng, applyToAll, newTargetTitle) =>
                    actions.handleResolveCoordinates(
                      activeRowId,
                      lat,
                      lng,
                      applyToAll,
                      newTargetTitle,
                    )
                  }
                  onClose={closeSubPanel}
                  onCancelBulk={actions.handleCancelBulkCoords}
                  onUpdatePhotos={(files, descColKeyToMark) => {
                    actions.setParsedData?.((prev) =>
                      prev.map((r) => {
                        if (r.id === activeRowId) {
                          const photoColName =
                            state?.columns?.find(
                              (c) =>
                                c.toLowerCase().includes('photo') ||
                                c.toLowerCase().includes('image'),
                            ) || 'Photos';

                          const updatedManuallyAssigned = {
                            ...(r._manuallyAssigned || {}),
                          };

                          if (descColKeyToMark) {
                            updatedManuallyAssigned[descColKeyToMark] = true;
                          } else {
                            updatedManuallyAssigned[photoColName] =
                              files && files.length > 0;
                          }

                          return {
                            ...r,
                            photoMatch: files && files.length > 0 ? 'MATCH' : 'NO_MATCH',
                            [photoColName]:
                              files && files.length > 0
                                ? files.length === 1
                                  ? files[0].name
                                  : `${files.length} photos`
                                : '',
                            matchedFiles: files || [],
                            _manuallyAssigned: updatedManuallyAssigned,
                          };
                        }
                        return r;
                      }),
                    );
                  }}
                  onUpdateRowDescription={(rowId, colKey, htmlText) => {
                    actions.setParsedData?.((prev) =>
                      prev.map((r) => {
                        if (r.id === rowId) {
                          return {
                            ...r,
                            [colKey]: htmlText,
                            _manuallyAssigned: {
                              ...(r._manuallyAssigned || {}),
                              [colKey]: true,
                            },
                          };
                        }
                        return r;
                      }),
                    );
                  }}
                  rowData={activeRowData}
                  searchTarget={
                    state?.resolveCoordsState?.searchTarget ||
                    state?.invalidMatchState?.searchTarget ||
                    ''
                  }
                  actualTitle={state?.resolveCoordsState?.actualTitle || ''}
                  geoSuffix={state?.resolveCoordsState?.geoSuffix || ''}
                  initialLat={activeLat}
                  initialLng={activeLng}
                  makeUppercase={state?.makeUppercase}
                  rteFontType={state?.rteFontType || 'Helvetica'}
                  rteFontSize={state?.rteFontSize || '10pt'}
                  rowMappings={state?.rowMappings}
                  rowSubMappings={state?.rowSubMappings}
                  customRows={state?.customRows}
                  isBulkGenerating={state?.resolveCoordsState?.isBulkGenerating || false}
                  userZoomLevel={state?.userZoomLevel}
                  onZoomChange={actions.setUserZoomLevel}>
                  {(isInvalidMatchActive ||
                    isManualMatchActive ||
                    isDocumentMatchActive) && (
                    <ExcelDialogs
                      state={state}
                      flags={flags}
                      alerts={alerts}
                      actions={actions}
                    />
                  )}
                </CoordinateMatchDialog>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* --- FOOTER PAGINATION --- */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 0.5,
          minHeight: '40px',
          borderTop: '1px solid black',
          backgroundImage: `linear-gradient(rgba(184, 231, 253, .85), rgba(184, 231, 253, .85)), url(${AdminBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          flexShrink: 0,
          position: 'relative',
        }}>
        <Box sx={{ minWidth: '150px', display: 'flex', alignItems: 'center' }}>
          {(state?.viewMode === 'DATA_VIEW' ||
            state?.viewMode === 'SETTINGS_VIEW' ||
            state?.viewMode === 'DUPLICATE_CHECK_VIEW') &&
            flags?.hasData && (
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#333' }}>
                Total Markers: {state?.parsedData?.length || 0}
              </Typography>
            )}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            gap: 1,
          }}>
          {STEPS.map((step) =>
            state?.viewMode === step ? (
              <CircleIcon key={step} sx={{ fontSize: '14px', color: '#2e7d32' }} />
            ) : (
              <CircleOutlinedIcon key={step} sx={{ fontSize: '14px', color: '#666' }} />
            ),
          )}
        </Box>

        <Box
          sx={{ display: 'flex', gap: 1, minWidth: '150px', justifyContent: 'flex-end' }}>
          <IconButton
            onClick={actions.handleNavFirst}
            disabled={state?.viewMode === 'MENU_VIEW' || state?.isProcessing}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <FirstPageIcon
              sx={{
                color: state?.viewMode === 'MENU_VIEW' ? 'grey' : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>

          <IconButton
            onClick={actions.handleNavBack}
            disabled={
              state?.viewMode === 'MENU_VIEW' || state?.isProcessing || isStepNavDisabled
            }
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <ArrowBackIosNewIcon
              sx={{
                color:
                  state?.viewMode === 'MENU_VIEW' || isStepNavDisabled ? 'grey' : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>

          <IconButton
            onClick={actions.handleNavForward}
            disabled={
              state?.viewMode === 'HISTORY_VIEW' ||
              state?.isProcessing ||
              isStepNavDisabled
            }
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <ArrowForwardIosIcon
              sx={{
                color:
                  state?.viewMode === 'HISTORY_VIEW' || isStepNavDisabled
                    ? 'grey'
                    : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>

          <IconButton
            onClick={actions.handleNavLast}
            disabled={state?.viewMode === 'HISTORY_VIEW' || state?.isProcessing}
            sx={{
              bgcolor: 'rgba(255,255,255,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' },
              '&.Mui-disabled': { opacity: 0.3 },
            }}>
            <LastPageIcon
              sx={{
                color: state?.viewMode === 'HISTORY_VIEW' ? 'grey' : 'black',
                fontSize: 22,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      <ImportPhotos
        isActive={state?.isPhotoFlowActive || false}
        parsedData={state?.parsedData || []}
        setParsedData={actions.setParsedData}
        columns={state?.columns || []}
        setColumns={actions.setColumns}
        onComplete={() => actions.setIsPhotoFlowActive(false)}
        setAlertState={actions.setAlertState}
        setStagedFiles={actions.setStagedFiles}
        setIsProcessing={actions.setIsProcessing}
      />

      <ConfirmDialog
        open={alerts?.showBatchFilenameDialog || false}
        onClose={() => actions.setShowBatchFilenameDialog(false)}
        onConfirm={() => {
          if (typeof actions.confirmImportBatch === 'function') {
            actions.confirmImportBatch(tempBatchFilename);
          }
        }}
        message={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography>
              Please confirm or edit the Batch Filename for this import:
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size='small'
              variant='outlined'
              label='Batch Filename'
              value={tempBatchFilename}
              onChange={(e) => setTempBatchFilename(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (typeof actions.confirmImportBatch === 'function') {
                    actions.confirmImportBatch(e.target.value);
                  }
                }
              }}
              sx={{
                '& .MuiInputBase-input': {
                  textTransform: 'none !important',
                },
              }}
            />
          </Box>
        }
        severity='info'
        confirmText='OK'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts?.confirmDialog?.open || false}
        onClose={() =>
          actions.setConfirmDialog({ ...(alerts?.confirmDialog || {}), open: false })
        }
        onConfirm={() => {
          if (alerts?.confirmDialog?.onConfirm) {
            alerts.confirmDialog.onConfirm();
          } else if (typeof actions.executeDeleteSettingsOrData === 'function') {
            actions.executeDeleteSettingsOrData();
          }
        }}
        message={alerts?.confirmDialog?.message || ''}
        severity={alerts?.confirmDialog?.severity || 'info'}
        confirmText={alerts?.confirmDialog?.confirmText || 'Yes'}
        cancelText={alerts?.confirmDialog?.cancelText || 'No'}
        hideCancel={alerts?.confirmDialog?.hideCancel || false}
        hideConfirm={alerts?.confirmDialog?.hideConfirm || false}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
      />

      <ConfirmDialog
        open={alerts?.showHistoryDeleteConfirm || false}
        onClose={() => actions.setShowHistoryDeleteConfirm(false)}
        onConfirm={() => {
          actions.setShowHistoryDeleteConfirm(false);
          actions.setShowHistoryDeleteConfirm2(true);
        }}
        message={`This will delete ${totalMarkersToDelete} markers in the ${batchText} you have selected. Do you want to proceed?`}
        severity='warning'
        confirmText='OK'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts?.showHistoryDeleteConfirm2 || false}
        onClose={() => actions.setShowHistoryDeleteConfirm2(false)}
        onConfirm={() => {
          actions.setShowHistoryDeleteConfirm2(false);
          if (typeof actions.executeDeleteBatches === 'function') {
            actions.executeDeleteBatches();
          }
        }}
        message={`Are you absolutely sure you want to permanently delete these ${totalMarkersToDelete} markers? This action cannot be undone.`}
        severity='error'
        confirmText='Yes, Delete'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts?.showPublishConfirm1 || false}
        onClose={() => actions.setShowPublishConfirm1(false)}
        onConfirm={() => {
          actions.setShowPublishConfirm1(false);
          if (typeof actions.executePublishBatches === 'function') {
            actions.executePublishBatches();
          }
        }}
        message='This will remove the batch from this list and move it to Archived Batches.'
        severity='warning'
        confirmText='OK'
        cancelText='Cancel'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts?.alertState?.open || false}
        onClose={() => actions.setAlertState((prev) => ({ ...prev, open: false }))}
        message={alerts?.alertState?.message || ''}
        severity={alerts?.alertState?.severity || 'info'}
        hideCancel={true}
        hideConfirm={true}
        autoClose={3000}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />
    </Box>
  );
}
